import React, { useRef, useState } from "react";
import { Button } from "./ui/button";
import { Upload, Camera, X, FileText } from "lucide-react";

const MAX_PDF = 3 * 1024 * 1024;

async function shrinkImage(file) {
  const url = URL.createObjectURL(file);
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = url; });
  const scale = Math.min(1, 1400 / Math.max(img.width, img.height));
  const c = document.createElement("canvas");
  c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  URL.revokeObjectURL(url);
  return c.toDataURL("image/jpeg", 0.82);
}

export function FileUpload({ value, onChange, label, required, testid = "upload" }) {
  const fileRef = useRef(null);
  const camRef = useRef(null);
  const [err, setErr] = useState("");
  const pick = async (file) => {
    if (!file) return;
    setErr("");
    try {
      if (file.type.startsWith("image/")) onChange({ name: file.name || "photo.jpg", type: "image/jpeg", data: await shrinkImage(file) });
      else if (file.type === "application/pdf") {
        if (file.size > MAX_PDF) return setErr("PDF is too large (max 3 MB). Take a photo of the document instead.");
        const data = await new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(file); });
        onChange({ name: file.name, type: "application/pdf", data });
      } else setErr("Please attach a photo (JPG/PNG) or a PDF.");
    } catch { setErr("Could not read that file. Try another photo."); }
  };
  return (
    <div className="space-y-2">
      <span className="text-sm font-medium">{label}{required && " *"}</span>
      {value ? (
        <div className="flex items-center gap-3 rounded-[9px] border border-border p-2" data-testid={`${testid}-preview`}>
          {value.type === "application/pdf" ? <FileText className="h-12 w-12 text-primary" /> : <img src={value.data} alt="" className="h-16 w-20 rounded object-cover" />}
          <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium">{value.name}</p><p className="text-xs text-muted-foreground">Attached</p></div>
          <Button type="button" size="icon" variant="ghost" onClick={() => onChange(null)} aria-label="Remove" data-testid={`${testid}-remove`}><X className="h-4 w-4" /></Button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" className="h-12" onClick={() => camRef.current?.click()} data-testid={`${testid}-camera`}><Camera className="mr-2 h-4 w-4" /> Take photo</Button>
          <Button type="button" variant="outline" className="h-12" onClick={() => fileRef.current?.click()} data-testid={`${testid}-file`}><Upload className="mr-2 h-4 w-4" /> Upload file</Button>
        </div>
      )}
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => pick(e.target.files?.[0])} />
      <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid={`${testid}-input`} />
      {err && <p className="text-xs text-[#A4473C]">{err}</p>}
    </div>
  );
}
